import { PortableText, type PortableTextComponents } from '@portabletext/react';
import { type PortableTextBlock } from '@portabletext/types';

import { Header } from '../../atoms/header/header';

type ContentBlock = {
  _key?: string;
  _type: 'contentBlock';
  body?: Array<PortableTextBlock | InlineVideoBlock>;
  image?: {
    _type: 'image';
    asset?: {
      _ref: string;
      _type: 'reference';
    };
  };
  imageUrl?: string | null;
  title?: string;
};

type InlineVideoBlock = {
  _key?: string;
  _type: 'inlineVideo';
  externalUrl: string | null;
  videoUrl: string | null;
  caption: string;
};

type FileLinkMark = {
  fileName?: string;
  fileUrl?: string;
};

type ArticleContent = {
  body: Array<PortableTextBlock | ContentBlock>;
  image: string | null;
  title: string;
};

type ContentArticleProps = {
  readonly article: ArticleContent;
};

const isEmptyBlock = (block?: PortableTextBlock) => {
  if (!block || !('children' in block) || !Array.isArray(block.children)) {
    return false;
  }

  return block.children.every((child) => {
    if (!child || typeof child !== 'object' || !('text' in child)) {
      return false;
    }

    return typeof child.text === 'string' && child.text.trim() === '';
  });
};

const FileLinkRenderer = ({
  children,
  value,
}: {
  readonly children: React.ReactNode;
  readonly value?: FileLinkMark;
}) => {
  const fileLink = value;
  if (!fileLink?.fileUrl) return children;

  return (
    <a href={fileLink.fileUrl} rel="noreferrer" target="_blank">
      {children}
    </a>
  );
};

const ContentBlockRenderer = ({ value }: { readonly value?: ContentBlock }) => {
  if (!value) return null;

  return (
    <section className="mt-2">
      {value.imageUrl ? (
        <img alt={value.title ?? 'Article content image'} height={48} src={value.imageUrl} width={48} />
      ) : null}
      {value.title ? <Header level="h2">{value.title}</Header> : null}
      {value.body ? <PortableText components={components} value={value.body} /> : null}
    </section>
  );
};

const InlineVideoRenderer = ({ value }: { readonly value: InlineVideoBlock }) => {
  const { externalUrl, videoUrl, caption } = value;

  if (externalUrl) {
    return (
      <figure>
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          aria-label={caption}
          className="w-full"
          referrerPolicy="strict-origin-when-cross-origin"
          src={externalUrl}
          style={{ aspectRatio: '16/9' }}
          title={caption}
        />
      </figure>
    );
  }

  if (!videoUrl) return null;

  return (
    <figure>
      <video
        aria-label={caption}
        className="w-full"
        controls
        preload="metadata"
        src={videoUrl}
        style={{ aspectRatio: '16/9' }}
      />
    </figure>
  );
};

const components: PortableTextComponents = {
  block: {
    normal: ({ children, value }: { children?: React.ReactNode; value?: PortableTextBlock }) => {
      if (isEmptyBlock(value)) {
        return (
          <p aria-hidden="true" style={{ minHeight: '1em' }}>
            &nbsp;
          </p>
        );
      }

      return <p>{children}</p>;
    },
  },
  marks: {
    fileLink: FileLinkRenderer,
  },
  types: {
    contentBlock: ContentBlockRenderer,
    inlineVideo: InlineVideoRenderer,
  },
};

const ContentArticle = ({ article }: ContentArticleProps) => {
  return (
    <article className="prose">
      <Header level="h1">{article.title}</Header>
      {article.image ? <img alt={article.title} src={article.image} /> : null}

      <PortableText components={components} value={article.body} />
    </article>
  );
};

export type { ArticleContent };
export { ContentArticle, ContentBlockRenderer, isEmptyBlock };
